import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import ListingItem from "../ListingItem";
import Curtain from "../Curtain";
import SortOrder from "../Search/SortOrder";
import { useSearchData } from "../../hooks/useSearchData";
import Pagnation from "../Pagination";

/**
 * Renders a paginated listing component with dynamic data and pagination functionality.
 *
 * @param {string} title - The title of the listing component.
 * @param {string} listingItemClasses - The CSS classes for each listing item.
 * @param {object} initialData - The initial data to populate the listing with.
 * @param {boolean} hideSortOrder - Flag to hide the sort order component.
 * @param {boolean} showNicheItem - Flag to show niche items.
 * @param {boolean} appendPageNumberToUrl - Flag to append page number to URL.
 * @param {boolean} isOffmarket - Flag to indicate if the listings are off-market.
 * @return {JSX.Element} The paginated listing component.
 */
const PagnatedListing = ({
    title = undefined,
    listingItemClasses = "col-sm-12 col-md-12 col-lg-4",
    initialData,
    hideSortOrder = false,
    showNicheItem = true,
    appendPageNumberToUrl = true,
    isOffmarket = false,
}) => {
    const isFetching = useSelector((state) => state.search.isFetching);
    const { resultsData } = useSearchData();
    const {
        page_number = 1,
        page_count,
        pages,
        results,
        paginationCreditText,
    } = resultsData || initialData || {};
    const history = useHistory();
    const { three: urlPageNumber } = useParams();
    const pageList = pages?.[page_number || 1] || results;
    const isPaginated = page_count && page_count > 1;

    // This might be a little brittle...not quite sure yet.
    // I am checking if the 3rd param is a number and if so, performing the logic.
    // This needs to be done so that we will remove a page number if it is no longer a page.
    useEffect(() => {
        if (appendPageNumberToUrl) {
            if (
                urlPageNumber &&
                !isNaN(parseInt(urlPageNumber)) &&
                urlPageNumber !== page_number.toString()
            ) {
                updatedPathWithPage(undefined);
            }
        }
        // eslint-disable-next-line
    }, []);

    const updatedPathWithPage = (pageToUse) => {
        const pathArray = history.location.pathname.split("/");
        const lastItem = parseInt(pathArray[pathArray.length - 1]);
        let pathname;
        if (!pageToUse) {
            pathname = `${pathArray.slice(0, -1).join("/")}`;
        } else if (lastItem) {
            pathname = `${pathArray.slice(0, -1).join("/")}/${pageToUse}`;
        } else {
            pathname = `${pathArray.join("/")}/${pageToUse}`;
        }
        history.replace({
            pathname,
            state: history.location.state,
        });
    };

    return (
        <Curtain show={isFetching}>
            <div className="container-fluid g-0" data-test="paginated-listings">
                {!pageList || pageList.length === 0 ? (
                    <div>No listings found</div>
                ) : (
                    <>
                        {title && (
                            <div className="row g-0">
                                <div className="col g-0">
                                    <h5>{title}</h5>
                                </div>
                            </div>
                        )}
                        <div className="row  g-0">
                            <div className="col d-flex px-0 mx-1 flex-wrap align-items-center">
                                <div className="me-2">
                                    <div
                                        className="small"
                                        data-test="page-numbers"
                                    >
                                        Page {page_number} of{" "}
                                        {page_count || "1"}
                                    </div>
                                </div>
                                {!hideSortOrder && (
                                    <span className="d-flex ms-auto align-items-center">
                                        <span className="me-1">Sort By:</span>
                                        <SortOrder />
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="row g-0 mt-1">
                            {pageList.map((listing) => (
                                <ListingItem
                                    key={`listing-item-${listing.listingId}`}
                                    listing={listing}
                                    className={`listing-item px-0 mb-2 ${listingItemClasses}`}
                                    showNicheItem={showNicheItem}
                                    showSave={!isOffmarket}
                                />
                            ))}
                        </div>
                        {isPaginated && (
                            <Pagnation
                                page_count={page_count}
                                page_number={page_number}
                            />
                        )}
                        {paginationCreditText &&
                            paginationCreditText.length > 0 && (
                                <div className="pagination-credits">
                                    {paginationCreditText.map(
                                        (creditItem, i) => (
                                            <div
                                                key={`credit-item-${i}`}
                                                dangerouslySetInnerHTML={{
                                                    __html: creditItem,
                                                }}
                                            />
                                        )
                                    )}
                                </div>
                            )}
                    </>
                )}
            </div>
        </Curtain>
    );
};

export default PagnatedListing;
